import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { refreshTokenIfNeeded } from '../utils/axiosConfig';
import apiService from '../services/apiService';
import { isDemoMode } from '../config';
// We're using this component in the JSX below, so this import is needed
import OpportunityChart from './OpportunityChart';
import './OpportunityChart.css';
import { useToast } from './ToastProvider';
import * as XLSX from 'xlsx';
import bmailogo from '../assets/bmailogo.png';

// Helper function to capitalize the first letter of domain name
const capitalizeFirstLetter = (domain) => {
  if (!domain) return '';
  return domain.charAt(0).toUpperCase() + domain.slice(1);
};

// Create a logger utility for AdminDashboard component
const logger = {
  debug: (message, data) => {
    console.debug(`[AdminDashboard][DEBUG] ${message}`, data || '');
  },
  info: (message, data) => {
    console.info(`[AdminDashboard][INFO] ${message}`, data || '');
  },
  warn: (message, data) => {
    console.warn(`[AdminDashboard][WARN] ${message}`, data || '');
  },
  error: (message, data) => {
    console.error(`[AdminDashboard][ERROR] ${message}`, data || '');
  }
};

const AdminDashboard = () => {
  const [submissions, setSubmissions] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('submissions');
  const [selectedSubmissionId, setSelectedSubmissionId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedDomainForEmail, setSelectedDomainForEmail] = useState(null);
  const [domainEmails, setDomainEmails] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [loadingEmails, setLoadingEmails] = useState(false);
  // Delete confirmation modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [deletingSubmission, setDeletingSubmission] = useState(false);
  // Survey invitation states
  const [surveyEmail, setSurveyEmail] = useState('');
  const surveyMessageRef = useRef(null);
  const defaultMessage = `Dear [Recipient's Name],

At Bytemethod.ai, we specialize in helping organizations realize measurable value from their AI initiatives. We would like to invite your team to participate in a AI use case assessment using our proprietary AI Assessment Framework.

This framework has been designed to:
• Evaluate each AI use case individually across multiple dimensions such Quick Wins, Strategic Challenges, Technical Experiments, Lower Priority
• Provide data-driven, visual comparisons to prioritize use cases based on your strategic goals.
• Aggregate insights at the organizational level, enabling a clear, structured AI roadmap aligned with your business objectives.

Whether you're just starting your AI journey or looking to scale with confidence, our framework offers clarity on where to focus for maximum ROI.

We would be happy to schedule a brief call to walk you through the process and demonstrate how similar organizations have benefited from this assessment via Calendar or email.

Looking forward to exploring how we can support your AI strategy.

Best regards,

Bushra Chowdhury, PhD
AI Program Manager
https://bytemethod.ai
bushra.chowdhury@bytemethod.ai
o 703 752 7900| c 540 200 9957
8270 Greensboro Drive
Suite 1000
McLean VA, 22102
USA`;
  const [characterCount, setCharacterCount] = useState(defaultMessage.length);
  const [sendingSurveyInvitation, setSendingSurveyInvitation] = useState(false);
  // Survey results email modal states
  const [showSurveyResultsModal, setShowSurveyResultsModal] = useState(false);
  const [surveyResultsToField, setSurveyResultsToField] = useState('');
  const [surveyResultsCcField, setSurveyResultsCcField] = useState('');
  const [surveyResultsSubject, setSurveyResultsSubject] = useState('');
  const [surveyResultsMessage, setSurveyResultsMessage] = useState('');
  const [sendingSurveyResults, setSendingSurveyResults] = useState(false);
  const [selectedDomainForResults, setSelectedDomainForResults] = useState(null);
  const [loadingSurveyResultsEmails, setLoadingSurveyResultsEmails] = useState(false);
  const [availableEmailsForResults, setAvailableEmailsForResults] = useState([]);
  const [selectedResultsEmails, setSelectedResultsEmails] = useState([]);
  // Bulk upload states
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const fileInputRef = useRef(null);
  // Submission email modal states
  const [showSubmissionEmailModal, setShowSubmissionEmailModal] = useState(false);
  const [submissionEmailToField, setSubmissionEmailToField] = useState('');
  const [submissionEmailCcField, setSubmissionEmailCcField] = useState('');
  const [submissionEmailSubject, setSubmissionEmailSubject] = useState('');
  const [submissionEmailMessage, setSubmissionEmailMessage] = useState('');
  const [sendingSubmissionEmail, setSendingSubmissionEmail] = useState(false);
  const [selectedSubmissionForEmail, setSelectedSubmissionForEmail] = useState(null);
  // Selected surveys for combined chart
  const [selectedSurveys, setSelectedSurveys] = useState([]);
  const [showCombinedChart, setShowCombinedChart] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    logger.info('AdminDashboard component mounted');
    const token = localStorage.getItem('admin_token');
    const refreshToken = localStorage.getItem('admin_refresh_token');
    
    // In demo mode, allow access even without real tokens
    if (!isDemoMode && !token && !refreshToken) {
      logger.warn('No admin tokens found, redirecting to login');
      navigate('/admin');
      return;
    }
    
    // Prevent double API calls in StrictMode development
    let isStale = false;
    
    logger.debug('Admin tokens found (or demo mode), proceeding with dashboard initialization');
    
    const initializeDashboard = async () => {
      if (!isStale) {
        await loadDashboardData();
      }
    };
    
    initializeDashboard();
    
    // Set up periodic token refresh check (every 5 minutes) - skip in demo mode
    let tokenRefreshInterval;
    if (!isDemoMode) {
      tokenRefreshInterval = setInterval(async () => {
        try {
          if (!isStale) {
            await refreshTokenIfNeeded();
          }
        } catch (error) {
          logger.error('Periodic token refresh failed:', error);
        }
      }, 5 * 60 * 1000); // 5 minutes
    }
    
    return () => {
      logger.debug('AdminDashboard component unmounted');
      isStale = true;
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);



  const loadDashboardData = async () => {
    logger.info('Loading dashboard data');
    setLoading(true);
    try {
      // Check and refresh token if needed before making API calls (skip in demo mode)
      if (!isDemoMode) {
        await refreshTokenIfNeeded();
      }

      logger.debug('Making parallel API calls to fetch dashboard data');
      const [submissionsData, analyticsData, companiesData] = await Promise.all([
        apiService.getSubmissions(),
        apiService.getAnalytics(),
        apiService.getCompanies()
      ]);

      logger.debug('Processing submissions response', { 
        count: submissionsData?.length || 0
      });
      setSubmissions(submissionsData || []);
      
      logger.debug('Processing analytics response', { 
        hasData: !!analyticsData
      });
      setAnalytics(analyticsData);
      
      logger.debug('Processing companies response', { 
        count: companiesData?.length || 0
      });
      setCompanies(companiesData || []);
      
      logger.info('Dashboard data loaded successfully');
    } catch (error) {
      logger.error('Error loading dashboard data', { 
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      
      if (error.response?.status === 401) {
        // Token expired or invalid
        logger.warn('Authentication failed (401), redirecting to login');
        localStorage.removeItem('admin_token');
        navigate('/admin');
        return;
      }
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
      logger.debug('Finished loading dashboard data attempt');
    }
  };

  const handleLogout = () => {
    logger.info('Admin logout initiated');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_token_expires_at');
    navigate('/admin');
  };

  // Handle survey selection for combined chart
  const handleSurveySelection = (submissionId, isChecked) => {
    logger.debug('Survey selection changed', { submissionId, isChecked });
    
    if (isChecked) {
      setSelectedSurveys(prev => [...prev, submissionId]);
    } else {
      setSelectedSurveys(prev => prev.filter(id => id !== submissionId));
    }
  };

  // Handle select all surveys
  const handleSelectAllSurveys = (isChecked) => {
    logger.debug('Select all surveys changed', { isChecked });
    
    if (isChecked) {
      setSelectedSurveys(submissions.map(s => s.submission_id));
    } else {
      setSelectedSurveys([]);
    }
  };

  // Clear selected surveys
  const clearSelectedSurveys = () => {
    logger.debug('Clearing selected surveys');
    setSelectedSurveys([]);
    setShowCombinedChart(false);
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    const newMode = !selectionMode;
    logger.debug('Toggling selection mode', { newMode });
    setSelectionMode(newMode);
    
    // Clear selections when exiting selection mode
    if (!newMode) {
      setSelectedSurveys([]);
      setShowCombinedChart(false);
    }
  };

  // Show combined chart for selected surveys
  const showCombinedChartView = () => {
    if (selectedSurveys.length === 0) {
      showWarning('Please select at least one survey to create a combined chart');
      return;
    }
    
    logger.info('Showing combined chart', { selectedSurveyCount: selectedSurveys.length });
    setShowCombinedChart(true);
    setActiveTab('combined-chart');
  };

  const openSubmissionEmailModal = (submission) => {
    logger.info('Opening submission email modal', { submissionId: submission.submission_id });
    document.body.style.overflow = 'hidden';
    
    setSelectedSubmissionForEmail(submission);
    setShowSubmissionEmailModal(true);
    
    // Pre-populate the To field with the submission email
    setSubmissionEmailToField(submission.email);
    
    // Set default subject
    setSubmissionEmailSubject('AI Use Case Assessment Results & Strategic Insights from Bytemethod.ai');
    
    // Set default message
    setSubmissionEmailMessage(`Hello,

Thank you for the opportunity to collaborate on your AI use case assessment. We're pleased to share that the evaluation has been successfully completed using Bytemethod.ai's AI Assessment Framework.

Based on the data and inputs from your team, we've:
• Individually assessed your AI use case across critical dimensions including Quick Wins, Strategic Challenges, Technical Experiments, Lower Priority.
• Generated visual, data-driven comparisons to clearly identify high-impact opportunities.
• Consolidated insights to provide a unified view of your survey's AI maturity and strategic potential.

Key Outcomes:
• A prioritized assessment of your AI use case tailored to your business goals.
• Strategic positioning: ${submission.strategic_category}
• Detailed scoring across technical feasibility, ROI potential, and strategic value.

We're happy to schedule a session to walk you through the results and discuss how Bytemethod.ai can support your team in moving this use case forward into design and deployment.

Please let us know your availability, looking forward to the next phase of your AI journey.`);
    
    logger.debug('Submission email modal opened', { 
      submissionId: submission.submission_id,
      email: submission.email
    });
  };

  const closeSubmissionEmailModal = () => {
    logger.debug('Closing submission email modal');
    setShowSubmissionEmailModal(false);
    setSelectedSubmissionForEmail(null);
    setSubmissionEmailToField('');
    setSubmissionEmailCcField('');
    setSubmissionEmailSubject('');
    setSubmissionEmailMessage('');
    setSendingSubmissionEmail(false);
    document.body.style.overflow = 'unset';
  };

  const sendSubmissionEmail = async () => {
    if (!submissionEmailToField.trim() && !submissionEmailCcField.trim()) {
      showWarning('Please enter at least one recipient in the To or CC field');
      return;
    }

    if (!submissionEmailSubject.trim()) {
      showWarning('Please enter an email subject');
      return;
    }

    if (!submissionEmailMessage.trim()) {
      showWarning('Please enter an email message');
      return;
    }

    logger.info('Sending submission email', { 
      submissionId: selectedSubmissionForEmail.submission_id,
      toField: submissionEmailToField,
      ccField: submissionEmailCcField
    });

    try {
      setSendingSubmissionEmail(true);
      
      // Parse email addresses from To and CC fields
      const toEmails = submissionEmailToField.split(',').map(e => e.trim()).filter(e => e);
      const ccEmails = submissionEmailCcField.split(',').map(e => e.trim()).filter(e => e);
      
      const result = await apiService.sendSubmissionEmail({
        submission_id: selectedSubmissionForEmail.submission_id,
        to_emails: toEmails,
        cc_emails: ccEmails,
        subject: submissionEmailSubject.trim(),
        message: submissionEmailMessage.trim()
      });
      
      logger.info('Submission email sent', { 
        submissionId: selectedSubmissionForEmail.submission_id,
        result
      });

      // Check if there were any failures
      const totalAttempted = toEmails.length + ccEmails.length;
      const successfulCount = result.successful_sends?.length || result.recipients || 0;
      const failedEmails = result.failed_sends || [];
      
      if (failedEmails.length === 0) {
        showSuccess(`✅ Email sent successfully!`, 4000);
      } else if (successfulCount > 0) {
        // Some succeeded, some failed
        showWarning(`Email sent to ${successfulCount}/${totalAttempted} recipients. Some emails may have failed due to delivery restrictions.`, 6000);
        
        // Log failed emails for debugging
        if (failedEmails.length > 0) {
          logger.warn('Some emails failed to send:', failedEmails);
          console.warn('Failed email details:', failedEmails);
        }
      } else {
        // All failed - this should be caught by catch block
        showError(`Failed to send email. Please try again.`, 4000);
      }
      
      // Close modal after success
      setTimeout(() => {
        closeSubmissionEmailModal();
      }, 1500);
      
    } catch (error) {
      logger.error('Error sending submission email', { 
        submissionId: selectedSubmissionForEmail.submission_id,
        error: error.message,
        response: error.response?.data
      });
      
      showError(`Failed to send email. Please try again.`, 4000);
    } finally {
      setSendingSubmissionEmail(false);
      logger.debug('Submission email sending process completed');
    }
  };

  const exportCompanyCSV = async (domainName) => {
    logger.info('Exporting CSV for domain', { domainName });
    
    try {
      const response = await apiService.getCompanySubmissions(domainName);
      const companySubmissions = response.submissions || [];
      
      if (companySubmissions.length === 0) {
        showWarning(`No submissions found for ${domainName}`);
        return;
      }

      // Create Raw Data sheet
      const rawHeaders = [
        'Opportunity',
        'Description',
        'Email',
        'Automation Potential',
        'Infrastructure Requirements',
        'Integration Complexity',
        'Documentation and Requirements Clarity',
        'Technical Feasibility (YAxis)',
        'Time Spent on This Process',
        'Number of People Involved in This Process',
        'Consistency of Results from This Process',
        'ROI Potential (XAxis)',
        'Board-Level Alignment',
        'Brand Impact',
        'Organizational Goal Alignment',
        'Strategic Value (Radius Bubble)'
      ];

      const rawData = companySubmissions.map(sub => ([
        sub.opportunity_name || '',
        sub.opportunity_description || '',
        sub.email || '',
        sub.question_1_answer !== null && sub.question_1_answer !== undefined ? sub.question_1_answer : '',
        sub.question_2_answer !== null && sub.question_2_answer !== undefined ? sub.question_2_answer : '',
        sub.question_3_answer !== null && sub.question_3_answer !== undefined ? sub.question_3_answer : '',
        sub.question_4_answer !== null && sub.question_4_answer !== undefined ? sub.question_4_answer : '',
        sub.tech_feasibility_score !== null && sub.tech_feasibility_score !== undefined ? sub.tech_feasibility_score : '',
        sub.question_5_answer !== null && sub.question_5_answer !== undefined ? sub.question_5_answer : '',
        sub.question_6_answer !== null && sub.question_6_answer !== undefined ? sub.question_6_answer : '',
        sub.question_7_answer !== null && sub.question_7_answer !== undefined ? sub.question_7_answer : '',
        sub.roi_potential_score !== null && sub.roi_potential_score !== undefined ? sub.roi_potential_score : '',
        sub.question_8_answer !== null && sub.question_8_answer !== undefined ? sub.question_8_answer : '',
        sub.question_9_answer !== null && sub.question_9_answer !== undefined ? sub.question_9_answer : '',
        sub.question_10_answer !== null && sub.question_10_answer !== undefined ? sub.question_10_answer : '',
        sub.strategic_value_score !== null && sub.strategic_value_score !== undefined ? sub.strategic_value_score : ''
      ]));

      // Create Definitions sheet
      const definitions = [
        ['Question', 'Description', 'Scale'],
        ['', '', ''],
        ['Section 1: Technical Feasibility Assessment (Y-axis: -10 to +10)', '', ''],
        ['', '', ''],
        ['1. Process Automation Potential', 'How much of this process can be automated based on clear rules/patterns vs. requiring human judgment?', ''],
        ['', '+10', 'Can be fully automated with clear rules/patterns'],
        ['', '+8', ''],
        ['', '+6', 'Can be automated with minor human oversight needed'],
        ['', '+4', ''],
        ['', '+2', 'Can be automated with some human oversight needed'],
        ['', '0', 'Balanced mix of human and AI decisions'],
        ['', '-2', 'Can be automated but heavy human judgment required'],
        ['', '-4', ''],
        ['', '-6', 'Almost entirely human-dependent'],
        ['', '-8', ''],
        ['', '-10', 'Requires human input throughout the process'],
        ['', '', ''],
        ['2. Infrastructure Requirements', 'What are your technology deployment constraints?', ''],
        ['', '+10', 'Fully open to public cloud-based AI services'],
        ['', '+8', ''],
        ['', '+6', 'Open to public or private cloud, some data residency requirements'],
        ['', '+4', ''],
        ['', '+2', 'Limited cloud usage, some data residency requirements'],
        ['', '0', 'Balanced hybrid requirements, case-by-case evaluation'],
        ['', '-2', 'Strong on-premise preference, minimal cloud usage'],
        ['', '-4', ''],
        ['', '-6', 'Strict on-premise requirements, limited external services'],
        ['', '-8', ''],
        ['', '-10', 'Must be 100% on-premise, no external connectivity'],
        ['', '', ''],
        ['3. System Integration Complexity', 'How accessible are the required system integrations?', ''],
        ['', '+10', 'All systems have comprehensive REST APIs and MCP layers'],
        ['', '+8', ''],
        ['', '+6', 'Most systems have documented REST APIs, minor research needed'],
        ['', '+4', ''],
        ['', '+2', 'Most systems have base REST APIs, may need minor customization'],
        ['', '0', 'Balanced effort with some APIs and some custom work required'],
        ['', '-2', 'Few REST APIs available, mostly manual data exchange needed'],
        ['', '-4', ''],
        ['', '-6', 'Limited or older interfaces (SOAP, EDI), custom work required'],
        ['', '-8', ''],
        ['', '-10', 'No APIs available, major customization needed'],
        ['', '', ''],
        ['4. Documentation and Requirements Clarity', 'How well-defined is the current process?', ''],
        ['', '+10', 'Comprehensive documentation, fully standardized'],
        ['', '+8', ''],
        ['', '+6', 'Good documentation exists, minor requirements gaps'],
        ['', '+4', ''],
        ['', '+2', 'Adequate documentation, some requirements need clarification'],
        ['', '0', 'Basic documentation, moderate requirements gathering needed'],
        ['', '-2', 'Poor documentation, extensive requirements gathering needed'],
        ['', '-4', ''],
        ['', '-6', 'Very limited documentation, major process analysis required'],
        ['', '-8', ''],
        ['', '-10', 'No documentation exists, process completely undefined'],
        ['', '', ''],
        ['', '', ''],
        ['Section 2: ROI Potential Assessment (X-axis: -10 to +10)', '', ''],
        ['', '', ''],
        ['5. Time Spent on This Process', 'How much time do people currently spend completing this process?', ''],
        ['', '-10', 'No time required; already automated or negligible effort'],
        ['', '-8', ''],
        ['', '-6', 'Small amount of time required'],
        ['', '-4', ''],
        ['', '-2', 'Some time required'],
        ['', '0', 'Moderate amount of time required'],
        ['', '+2', 'Noticeable amount of time required'],
        ['', '+4', ''],
        ['', '+6', 'High amount of time required'],
        ['', '+8', ''],
        ['', '+10', 'Extremely time-consuming'],
        ['', '', ''],
        ['6. Number of People Involved in This Process', 'How many people or teams are usually involved in completing this process?', ''],
        ['', '-10', 'One person only; no coordination needed'],
        ['', '-8', ''],
        ['', '-6', 'One primary person with occasional help'],
        ['', '-4', ''],
        ['', '-2', 'Limited involvement across people'],
        ['', '0', 'Moderate number of people involved'],
        ['', '+2', 'Several people involved'],
        ['', '+4', ''],
        ['', '+6', 'Multiple teams involved'],
        ['', '+8', ''],
        ['', '+10', 'Large number of people and heavy coordination required'],
        ['', '', ''],
        ['7. Consistency of Results from This Process', 'How consistent are the results or data produced by this process today?', ''],
        ['', '-10', 'Always consistent and standardized'],
        ['', '-8', ''],
        ['', '-6', 'Mostly consistent with rare variation'],
        ['', '-4', ''],
        ['', '-2', 'Some inconsistency occurs'],
        ['', '0', 'Moderately consistent'],
        ['', '+2', 'Noticeable inconsistency'],
        ['', '+4', ''],
        ['', '+6', 'High inconsistency requiring corrections'],
        ['', '+8', ''],
        ['', '+10', 'Highly inconsistent and error-prone'],
        ['', '', ''],
        ['', '', ''],
        ['Section 3: Strategic Value Assessment (Bubble Size: 1-10)', '', ''],
        ['', '', ''],
        ['8. Board-Level Alignment', 'Does this project align with board-level strategic initiatives?', ''],
        ['', '10', 'Direct alignment with multiple critical board priorities'],
        ['', '9', ''],
        ['', '8', 'Clear alignment with major board objective'],
        ['', '7', ''],
        ['', '6', 'Good alignment with important board priority'],
        ['', '5', 'Some alignment with board priorities'],
        ['', '4', ''],
        ['', '3', 'Minimal connection to board priorities'],
        ['', '2', ''],
        ['', '1', 'No clear board-level alignment'],
        ['', '', ''],
        ['9. Brand Impact', 'Would this project positively impact your organization\'s brand?', ''],
        ['', '10', 'Transformational brand impact, major competitive advantage'],
        ['', '9', ''],
        ['', '8', 'Significant positive brand impact, strong differentiation'],
        ['', '7', ''],
        ['', '6', 'Positive brand effect, moderate differentiation'],
        ['', '5', 'Some positive brand effect, minor differentiation'],
        ['', '4', ''],
        ['', '3', 'Limited brand impact'],
        ['', '2', ''],
        ['', '1', 'No meaningful brand impact'],
        ['', '', ''],
        ['10. Organizational Goal Alignment', 'How well does this align with departmental/organizational goals?', ''],
        ['', '10', 'Perfect alignment with multiple strategic goals across organization'],
        ['', '9', ''],
        ['', '8', 'Strong alignment with major organizational goal(s)'],
        ['', '7', ''],
        ['', '6', 'Moderate alignment with organizational goal(s)'],
        ['', '5', 'Some alignment with departmental goal(s)'],
        ['', '4', ''],
        ['', '3', 'Limited alignment with current team objectives'],
        ['', '2', ''],
        ['', '1', 'Minimal to no clear alignment with current objectives']
      ];

      // Create workbook and worksheets
      const wb = XLSX.utils.book_new();
      
      // Add Raw Data sheet
      const wsRaw = XLSX.utils.aoa_to_sheet([rawHeaders, ...rawData]);
      XLSX.utils.book_append_sheet(wb, wsRaw, 'Raw Data');
      
      // Add Definitions sheet
      const wsDef = XLSX.utils.aoa_to_sheet(definitions);
      XLSX.utils.book_append_sheet(wb, wsDef, 'Definitions');
      
      // Log sheet names for debugging
      logger.info('Excel workbook sheets created', { 
        sheetNames: wb.SheetNames,
        sheetCount: wb.SheetNames.length 
      });
      
      // Generate and download
      const filename = `${domainName}_ai_assessment_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      logger.info('Company Excel export completed', { domainName, count: companySubmissions.length });
      showSuccess(`Excel file exported successfully!`, 4000);
      
    } catch (error) {
      logger.error('Error exporting company CSV', { 
        domainName,
        error: error.message,
        response: error.response?.data
      });
      showError(`Failed to export file. Please try again.`, 4000);
    }
  };

  const handleEmailSelection = (email, isChecked) => {
    logger.debug('Email selection changed', { email, isChecked });
    
    if (isChecked) {
      setSelectedEmails(prev => [...prev, email]);
    } else {
      setSelectedEmails(prev => prev.filter(e => e !== email));
    }
  };

  const handleSelectAllEmails = (isChecked) => {
    logger.debug('Select all emails changed', { isChecked });
    
    if (isChecked) {
      setSelectedEmails([...domainEmails]);
    } else {
      setSelectedEmails([]);
    }
  };

  const sendCompanyEmails = async () => {
    if (selectedEmails.length === 0) {
      showWarning('Please select at least one email address');
      return;
    }

    logger.info('Sending company emails', { 
      domain: selectedDomainForEmail, 
      emailCount: selectedEmails.length 
    });

    try {
      setSendingEmails(true);
      logger.info('Starting company email send process', {
        domain: selectedDomainForEmail,
        emailCount: selectedEmails.length,
        emails: selectedEmails
      });
      
      const result = await apiService.sendCompanyEmails(selectedDomainForEmail, selectedEmails);
      
      logger.info('Company emails sent', { 
        domain: selectedDomainForEmail,
        successful: result.successful_sends?.length || 0,
        failed: result.failed_sends?.length || 0,
        total_attempted: result.total_attempted || 0,
        result
      });

      showSuccess(`✅ Company emails sent successfully!`, 4000);

      // Close modal on success after a short delay
      setTimeout(() => {
        setShowEmailModal(false);
        setSelectedEmails([]);
        setDomainEmails([]);
        setSelectedDomainForEmail(null);
      }, 2000);
      
    } catch (error) {
      logger.error('Error sending company emails', { 
        domain: selectedDomainForEmail,
        error: error.message,
        response: error.response?.data,
        isTimeout: error.code === 'ECONNABORTED'
      });
      
      showError(`Failed to send company emails. Please try again.`, 4000);
    } finally {
      // Always clear sending state
      setSendingEmails(false);
      logger.debug('Email sending process completed, cleared sending state');
    }
  };

  const closeEmailModal = () => {
    logger.debug('Closing email modal');
    setShowEmailModal(false);
    setSelectedEmails([]);
    setDomainEmails([]);
    setSelectedDomainForEmail(null);
    setLoadingEmails(false);
  };

  const sendSurveyInvitation = async () => {
    if (!surveyEmail.trim()) {
      showWarning('Please enter an email address');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(surveyEmail.trim())) {
      showWarning('Please enter a valid email address');
      return;
    }

    const messageContent = surveyMessageRef.current?.value || defaultMessage;
    
    // Check if the message still contains the placeholder
    if (messageContent.includes('[Recipient\'s Name]')) {
      showWarning('Please replace "[Recipient\'s Name]" with the actual recipient name in your message before sending.');
      return;
    }
    logger.info('Sending survey invitation', { 
      email: surveyEmail.trim(), 
      hasMessage: !!messageContent.trim() 
    });

    try {
      setSendingSurveyInvitation(true);
      
      const result = await apiService.sendSurveyInvitation({
        email: surveyEmail.trim(),
        custom_message: messageContent.trim() || null
      });
      
      logger.info('Survey invitation sent successfully', { 
        email: surveyEmail.trim(),
        result
      });

      showSuccess(`✅ Survey invitation sent successfully!`, 4000);
      
      // Clear email field after successful send, but keep message for reuse
      setSurveyEmail('');
      
    } catch (error) {
      logger.error('Error sending survey invitation', { 
        email: surveyEmail.trim(),
        error: error.message,
        response: error.response?.data,
        isTimeout: error.code === 'ECONNABORTED'
      });
      
      showError(`Failed to send survey invitation. Please try again.`, 4000);
    } finally {
      setSendingSurveyInvitation(false);
      logger.debug('Survey invitation sending process completed');
    }
  };

  const openSurveyResultsModal = async (domainName) => {
    logger.info('Opening survey results email modal for domain', { domainName });
    document.body.style.overflow = 'hidden';
    
    try {
      setLoadingSurveyResultsEmails(true);
      setShowSurveyResultsModal(true);
      setSelectedDomainForResults(domainName);
      setSelectedResultsEmails([]);
      
      // Fetch emails for this domain
      const uniqueEmails = await apiService.getDomainEmails(domainName);
      
      if (uniqueEmails.length === 0) {
        showWarning(`No email addresses found for ${domainName}`);
        setShowSurveyResultsModal(false);
        return;
      }

      setAvailableEmailsForResults(uniqueEmails);
      
      // Pre-populate all emails in the To field
      setSurveyResultsToField(uniqueEmails.join(', '));
      setSelectedResultsEmails([...uniqueEmails]);
      
      // Set default subject and message
      setSurveyResultsSubject(`AI Use Case Assessment Results & Strategic Insights from Bytemethod.ai`);
      setSurveyResultsMessage(`Dear Team,
Thank you for the opportunity to collaborate on your AI use case assessment. We're pleased to share that the evaluation has been successfully completed using Bytemethod.ai's AI Assessment Framework.
Based on the data and inputs from your team, we've:
• Individually assessed each AI use case across critical dimensions including Quick Wins, Strategic Challenges, Technical Experiments, Lower Priority.
• Generated visual, data-driven comparisons to clearly identify high-impact opportunities.
• Consolidated insights across all use cases to provide a unified view of your organization's AI maturity and strategic potential.

Key Outcomes:
• A prioritized portfolio of AI use cases tailored to your business goals.
• A combined organizational view, highlighting clusters of opportunity, quick wins, and longer-term strategic bets.

We're happy to schedule a session to walk you through the results and discuss how Bytemethod.ai can support your team in moving selected use cases forward into design and deployment.

Please let us know your availability, looking forward to the next phase of your AI journey.`);
      
      logger.debug('Survey results email modal opened', { 
        domainName, 
        uniqueEmailCount: uniqueEmails.length 
      });
      
    } catch (error) {
      logger.error('Error loading emails for survey results modal', { 
        domainName,
        error: error.message,
        response: error.response?.data
      });
      showError(`Failed to load emails. Please try again.`, 4000);
      setShowSurveyResultsModal(false);
    } finally {
      setLoadingSurveyResultsEmails(false);
    }
  };

  const handleResultsEmailSelection = (email, isChecked) => {
    logger.debug('Results email selection changed', { email, isChecked });
    
    let updatedEmails;
    if (isChecked) {
      updatedEmails = [...selectedResultsEmails, email];
    } else {
      updatedEmails = selectedResultsEmails.filter(e => e !== email);
    }
    
    setSelectedResultsEmails(updatedEmails);
    setSurveyResultsToField(updatedEmails.join(', '));
  };

  const handleSelectAllResultsEmails = (isChecked) => {
    logger.debug('Select all results emails changed', { isChecked });
    
    if (isChecked) {
      setSelectedResultsEmails([...availableEmailsForResults]);
      setSurveyResultsToField(availableEmailsForResults.join(', '));
    } else {
      setSelectedResultsEmails([]);
      setSurveyResultsToField('');
    }
  };

  const sendSurveyResultsEmail = async () => {
    if (!surveyResultsToField.trim() && !surveyResultsCcField.trim()) {
      showWarning('Please enter at least one recipient in the To or CC field');
      return;
    }

    if (!surveyResultsSubject.trim()) {
      showWarning('Please enter an email subject');
      return;
    }

    if (!surveyResultsMessage.trim()) {
      showWarning('Please enter an email message');
      return;
    }

    logger.info('Sending survey results email', { 
      domain: selectedDomainForResults, 
      toField: surveyResultsToField,
      ccField: surveyResultsCcField
    });

    try {
      setSendingSurveyResults(true);
      
      // Parse email addresses from To and CC fields
      const toEmails = surveyResultsToField.split(',').map(e => e.trim()).filter(e => e);
      const ccEmails = surveyResultsCcField.split(',').map(e => e.trim()).filter(e => e);
      
      const result = await apiService.sendSurveyResultsEmail({
        domain_name: selectedDomainForResults,
        to_emails: toEmails,
        cc_emails: ccEmails,
        subject: surveyResultsSubject.trim(),
        message: surveyResultsMessage.trim()
      });
      
      logger.info('Survey results email sent', { 
        domain: selectedDomainForResults,
        result
      });

      // Check if there were any failures
      const totalAttempted = toEmails.length + ccEmails.length;
      const successfulCount = result.successful_sends?.length || result.recipients || 0;
      const failedEmails = result.failed_sends || [];
      
      if (failedEmails.length === 0) {
        showSuccess(`✅ Survey results email sent successfully!`, 4000);
      } else if (successfulCount > 0) {
        // Some succeeded, some failed
        showWarning(`Email sent to ${successfulCount}/${totalAttempted} recipients. Some emails may have failed due to delivery restrictions.`, 6000);
        
        // Log failed emails for debugging
        if (failedEmails.length > 0) {
          logger.warn('Some emails failed to send:', failedEmails);
          console.warn('Failed email details:', failedEmails);
        }
      } else {
        // All failed - this should be caught by catch block
        showError(`Failed to send survey results email. Please try again.`, 4000);
      }
      
      // Close modal after success
      setTimeout(() => {
        closeSurveyResultsModal();
      }, 1500);
      
    } catch (error) {
      logger.error('Error sending survey results email', { 
        domain: selectedDomainForResults,
        error: error.message,
        response: error.response?.data
      });
      
      showError(`Failed to send survey results email. Please try again.`, 4000);
    } finally {
      setSendingSurveyResults(false);
      logger.debug('Survey results email sending process completed');
    }
  };

  const closeSurveyResultsModal = () => {
    logger.debug('Closing survey results email modal');
    setShowSurveyResultsModal(false);
    setSelectedResultsEmails([]);
    setAvailableEmailsForResults([]);
    setSelectedDomainForResults(null);
    setSurveyResultsToField('');
    setSurveyResultsCcField('');
    setSurveyResultsSubject('');
    setSurveyResultsMessage('');
    setLoadingSurveyResultsEmails(false);
    document.body.style.overflow = 'unset';
  };

  const openDeleteModal = (submission) => {
    logger.info('Opening delete confirmation modal', { submissionId: submission.submission_id });
    setSubmissionToDelete(submission);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    logger.debug('Closing delete confirmation modal');
    setShowDeleteModal(false);
    setSubmissionToDelete(null);
  };

  const deleteSubmission = async () => {
    if (!submissionToDelete) {
      logger.warn('No submission selected for deletion');
      return;
    }

    const submissionId = submissionToDelete.submission_id;
    const opportunityName = submissionToDelete.opportunity_name;
    const userEmail = submissionToDelete.email;

    logger.info('Deleting submission', { 
      submissionId, 
      opportunityName, 
      userEmail 
    });

    try {
      setDeletingSubmission(true);
      
      const result = await apiService.deleteSubmission(submissionId);
      
      logger.info('Submission deleted successfully', { 
        submissionId,
        result
      });

      // Update the submissions list by removing the deleted submission
      setSubmissions(prevSubmissions => 
        prevSubmissions.filter(sub => sub.submission_id !== submissionId)
      );

      // Clear selection if this was the selected submission
      if (selectedSubmissionId === submissionId) {
        setSelectedSubmissionId(null);
        // Switch to submissions tab if we were viewing the deleted submission's chart
        if (activeTab === 'chart') {
          setActiveTab('submissions');
        }
      }

      // Close modal
      setShowDeleteModal(false);
      setSubmissionToDelete(null);
      
      showSuccess(`✅ Submission deleted successfully!`, 4000);
      
    } catch (error) {
      logger.error('Error deleting submission', { 
        submissionId,
        error: error.message,
        response: error.response?.data,
        isTimeout: error.code === 'ECONNABORTED'
      });
      
      if (error.response?.status === 404) {
        showError(`Submission not found or already deleted.`, 4000);
        // Still remove from local state if not found
        setSubmissions(prevSubmissions => 
          prevSubmissions.filter(sub => sub.submission_id !== submissionId)
        );
        closeDeleteModal();
      } else if (error.code === 'ECONNABORTED') {
        showError(`Delete request timed out. Please try again.`, 4000);
      } else {
        showError(`Failed to delete submission. Please try again.`, 4000);
      }
    } finally {
      setDeletingSubmission(false);
      logger.debug('Delete submission process completed');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return 'Invalid Date';
    }
  };

  const getCategoryColor = (category) => {
    // These should match the backend colors in main.py
    const colors = {
      'Quick Wins': '#36b37e',      // Green: Tech > median AND ROI > median
      'Strategic Challenges': '#ff5630', // Red-Orange: Tech ≤ median AND ROI > median
      'Technical Experiments': '#0065ff', // Blue: Tech > median AND ROI ≤ median
      'Lower Priority': '#6b778c'    // Gray: Tech ≤ median AND ROI ≤ median
    };
    return colors[category] || '#333';
  };

  // Handle Excel file upload for bulk survey data
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.match(/\.(xlsx|xls)$/)) {
      showError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    
    try {
      // Read Excel file
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      
      // Look for "Raw Data" sheet (similar to export format)
      let worksheet;
      if (workbook.SheetNames.includes('Raw Data')) {
        worksheet = workbook.Sheets['Raw Data'];
      } else {
        // Fallback to first sheet
        worksheet = workbook.Sheets[workbook.SheetNames[0]];
      }
      
      // Convert to JSON
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length < 2) {
        throw new Error('Excel file must contain at least a header row and one data row');
      }
      
      // Process the Excel data
      const result = await processExcelData(jsonData);
      
      if (result.successful_count > 0) {
        showSuccess(`Successfully uploaded ${result.successful_count} submissions!`);
        // Refresh dashboard data
        await loadDashboardData();
      }
      
      if (result.failed_count > 0) {
        showWarning(`${result.failed_count} submissions failed. Please check the console for details.`);
        console.log('Failed submissions:', result.failed_submissions);
      }
      
    } catch (error) {
      console.error('Error uploading file:', error);
      showError(`Upload failed: ${error.message}`);
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Process Excel data and send to backend
  const processExcelData = async (jsonData) => {
    const headers = jsonData[0];
    const dataRows = jsonData.slice(1);
    
    // Expected headers based on the export format
    const expectedHeaders = {
      opportunity: ['Opportunity', 'opportunity_name', 'name'],
      description: ['Description', 'opportunity_description', 'description'],
      email: ['Email', 'email', 'user_email'],
      q1: ['Automation Potential', 'question_1', 'q1'],
      q2: ['Infrastructure Requirements', 'question_2', 'q2'],
      q3: ['Integration Complexity', 'question_3', 'q3'],
      q4: ['Documentation and Requirements Clarity', 'question_4', 'q4'],
      q5: ['Time Spent on This Process', 'question_5', 'q5'],
      q6: ['Number of People Involved in This Process', 'question_6', 'q6'],
      q7: ['Consistency of Results from This Process', 'question_7', 'q7'],
      q8: ['ROI Potential (XAxis)', 'question_8', 'q8'],
      q9: ['Board-Level Alignment', 'question_9', 'q9'],
      q10: ['Brand Impact', 'Organizational Goal Alignment', 'question_10', 'q10']
    };
    
    // Find column indices
    const columnMap = {};
    for (const [key, possibleNames] of Object.entries(expectedHeaders)) {
      for (const name of possibleNames) {
        const index = headers.findIndex(header => 
          header && header.toString().toLowerCase().includes(name.toLowerCase())
        );
        if (index !== -1) {
          columnMap[key] = index;
          break;
        }
      }
    }
    
    // Validate required columns
    const requiredColumns = ['opportunity', 'description', 'email', 'q1', 'q2', 'q3', 'q4', 'q5', 'q6', 'q7', 'q8', 'q9', 'q10'];
    const missingColumns = requiredColumns.filter(col => columnMap[col] === undefined);
    
    if (missingColumns.length > 0) {
      throw new Error(`Missing required columns: ${missingColumns.join(', ')}`);
    }
    
    const submissionsByDomain = {};
    
    // Group submissions by domain
    for (let i = 0; i < dataRows.length; i++) {
      const row = dataRows[i];
      if (!row || row.length === 0) continue;
      
      try {
        const email = row[columnMap.email]?.toString().trim();
        if (!email) continue;
        
        // Extract domain from email
        const domain = email.split('@')[1]?.toLowerCase();
        if (!domain) continue;
        
        const submission = {
          email: email,
          opportunity_name: row[columnMap.opportunity]?.toString().trim(),
          opportunity_description: row[columnMap.description]?.toString().trim(),
          question_1: parseInt(row[columnMap.q1]) || 0,
          question_2: parseInt(row[columnMap.q2]) || 0,
          question_3: parseInt(row[columnMap.q3]) || 0,
          question_4: parseInt(row[columnMap.q4]) || 0,
          question_5: parseInt(row[columnMap.q5]) || 0,
          question_6: parseInt(row[columnMap.q6]) || 0,
          question_7: parseInt(row[columnMap.q7]) || 0,
          question_8: parseInt(row[columnMap.q8]) || 1,
          question_9: parseInt(row[columnMap.q9]) || 1,
          question_10: parseInt(row[columnMap.q10]) || 1
        };
        
        // Validate submission data
        if (!submission.opportunity_description) {
          console.warn(`Skipping row ${i + 2}: Missing description`);
          continue;
        }
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(submission.email)) {
          console.warn(`Skipping row ${i + 2}: Invalid email format`);
          continue;
        }
        
        // Clamp question values to valid ranges
        for (let j = 1; j <= 7; j++) {
          const key = `question_${j}`;
          submission[key] = Math.max(-10, Math.min(10, submission[key]));
        }
        for (let j = 8; j <= 10; j++) {
          const key = `question_${j}`;
          submission[key] = Math.max(1, Math.min(10, submission[key]));
        }
        
        if (!submissionsByDomain[domain]) {
          submissionsByDomain[domain] = [];
        }
        submissionsByDomain[domain].push(submission);
        
      } catch (error) {
        console.warn(`Skipping row ${i + 2}: ${error.message}`);
        continue;
      }
    }
    
    // Upload submissions for each domain
    let totalSuccessful = 0;
    let totalFailed = 0;
    const allFailedSubmissions = [];
    
    for (const [domain, submissions] of Object.entries(submissionsByDomain)) {
      try {
        const result = await apiService.bulkUploadSurveys({
          domain_name: domain,
          submissions: submissions
        });
        totalSuccessful += result.successful_count || 0;
        totalFailed += result.failed_count || 0;
        
        if (result.failed_submissions) {
          allFailedSubmissions.push(...result.failed_submissions);
        }
        
      } catch (error) {
        console.error(`Failed to upload submissions for domain ${domain}:`, error);
        totalFailed += submissions.length;
        allFailedSubmissions.push(...submissions.map((sub, idx) => ({
          email: sub.email,
          error: `Upload failed for domain ${domain}: ${error.message}`
        })));
      }
    }
    
    return {
      successful_count: totalSuccessful,
      failed_count: totalFailed,
      failed_submissions: allFailedSubmissions
    };
  };

  // Helper functions for upload modal
  const closeUploadModal = () => {
    setShowUploadModal(false);
    document.body.style.overflow = 'unset';
  };

  const handleUploadFromModal = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Prevent background scrolling when modals are open
  useEffect(() => {
    if (showUploadModal || showSurveyResultsModal || showSubmissionEmailModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showUploadModal, showSurveyResultsModal, showSubmissionEmailModal]);



  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes modalFadeIn {
            0% { opacity: 0; }
            100% { opacity: 1; }
          }
          @keyframes modalSlideIn {
            0% { transform: translate(-50%, -60%) scale(0.9); opacity: 0; }
            100% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
          }
          .upload-tooltip:hover {
            opacity: 1 !important;
          }
          button:hover + .upload-tooltip {
            opacity: 1;
          }
          .modal-overlay {
            animation: modalFadeIn 0.3s ease-out;
          }
          .modal-content {
            animation: modalSlideIn 0.3s ease-out;
          }
        `}
      </style>
      <div className="container">
      <div className="header">
        <img 
          src={bmailogo} 
          alt="Bytemethod AI Logo" 
          style={{ 
            width: '300px', 
            height: 'auto', 
            marginBottom: '20px' 
          }} 
        />
        <h1>Admin Dashboard</h1>
        <p>Opportunity Assessment Survey Management</p>
      </div>

      {/* Navigation */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className={`btn ${activeTab === 'submissions' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('submissions')}
            >
              Submissions ({submissions.length})
            </button>
            <button
              className={`btn ${activeTab === 'companies' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('companies')}
            >
              Companies ({companies.length})
            </button>
            <button
              className={`btn ${activeTab === 'send-survey' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('send-survey')}
            >
              Send Survey
            </button>
            <button
              className={`btn ${activeTab === 'portfolio' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveTab('portfolio')}
            >
              Portfolio Chart
            </button>
            {(selectedSubmissionId || selectedCompany) && (
              <button
                className={`btn ${activeTab === 'chart' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setActiveTab('chart')}
                disabled={!selectedSubmissionId && !selectedCompany}
              >
                Selected Chart
              </button>
            )}
            {showCombinedChart && selectedSurveys.length > 0 && (
              <button
                className={`btn ${activeTab === 'combined-chart' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setActiveTab('combined-chart')}
              >
                Combined Chart ({selectedSurveys.length})
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
            <Link to="/" className="btn btn-secondary">
              View Survey
            </Link>
            <button className="btn btn-secondary" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="card">
          <div className="error-message" style={{ textAlign: 'center', fontSize: '16px' }}>
            {error}
          </div>
        </div>
      )}



      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="card">
          
          {/* Analytics Overview Section */}
          {analytics && (
            <div style={{ marginBottom: '30px' }}>
              <h3>Analytics Overview</h3>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h3 style={{ color: '#667eea', margin: '0 0 10px 0', fontSize: '2em' }}>
                    {analytics.total_submissions}
                  </h3>
                  <p style={{ margin: 0, fontWeight: '600' }}>Total Submissions</p>
                </div>
                
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h3 style={{ color: '#28a745', margin: '0 0 10px 0' }}>
                    {analytics.average_scores.tech_feasibility}
                  </h3>
                  <p style={{ margin: 0, fontWeight: '600' }}>Avg Tech Feasibility</p>
                </div>
                
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h3 style={{ color: '#007bff', margin: '0 0 10px 0' }}>
                    {analytics.average_scores.roi_potential}
                  </h3>
                  <p style={{ margin: 0, fontWeight: '600' }}>Avg ROI Potential</p>
                </div>
                
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <h3 style={{ color: '#ffc107', margin: '0 0 10px 0' }}>
                    {analytics.average_scores.strategic_value}
                  </h3>
                  <p style={{ margin: 0, fontWeight: '600' }}>Avg Strategic Value</p>
                </div>
              </div>

              <h4>Category Distribution</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '30px' }}>
                {Object.entries(analytics.category_distribution || {}).map(([category, count]) => (
                  <div 
                    key={category}
                    style={{
                      padding: '15px',
                      border: `2px solid ${getCategoryColor(category)}`,
                      borderRadius: '8px',
                      backgroundColor: `${getCategoryColor(category)}15`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <h4 style={{ color: getCategoryColor(category), margin: 0 }}>{category}</h4>
                      <span style={{ 
                        backgroundColor: getCategoryColor(category), 
                        color: 'white', 
                        padding: '4px 12px', 
                        borderRadius: '20px',
                        fontWeight: 'bold'
                      }}>
                        {count}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          {submissions.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>No submissions found.</p>
              <Link to="/" className="btn btn-primary">
                Take the First Assessment
              </Link>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              {/* Combined Chart Controls */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '15px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                border: '1px solid #dee2e6'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  {selectionMode ? (
                    <>
                      <div style={{ fontWeight: '600', color: '#333' }}>
                        {selectedSurveys.length} survey{selectedSurveys.length !== 1 ? 's' : ''} selected
                      </div>
                      {selectedSurveys.length > 0 && (
                        <button
                          className="btn btn-sm"
                          style={{ 
                            backgroundColor: '#6c757d', 
                            color: 'white',
                            padding: '6px 12px',
                            fontSize: '13px'
                          }}
                          onClick={clearSelectedSurveys}
                        >
                          Clear Selection
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      className="btn btn-primary"
                      style={{ 
                        padding: '8px 16px',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                      onClick={toggleSelectionMode}
                    >
                      Select Surveys for Combined Chart
                    </button>
                  )}
                </div>
                
                {selectionMode && (
                  <div style={{ display: 'flex', gap: '10px' }}>
                    {selectedSurveys.length > 0 && (
                      <button
                        className="btn btn-primary"
                        style={{ 
                          padding: '8px 20px',
                          fontSize: '14px',
                          fontWeight: '600'
                        }}
                        onClick={showCombinedChartView}
                      >
                        Create Combined Chart ({selectedSurveys.length})
                      </button>
                    )}
                    <button
                      className="btn btn-secondary"
                      style={{ 
                        padding: '8px 16px',
                        fontSize: '14px'
                      }}
                      onClick={toggleSelectionMode}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>

              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    {selectionMode && (
                      <th style={{ padding: '8px', textAlign: 'center', borderBottom: '2px solid #dee2e6', width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={selectedSurveys.length === submissions.length && submissions.length > 0}
                          onChange={(e) => handleSelectAllSurveys(e.target.checked)}
                          style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                          title="Select all surveys"
                        />
                      </th>
                    )}
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Email</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Opportunity</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Submitted</th>
                    <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', minWidth: '300px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((submission) => (
                    <tr 
                      key={submission.submission_id} 
                      onClick={() => {
                        if (selectionMode) {
                          handleSurveySelection(submission.submission_id, !selectedSurveys.includes(submission.submission_id));
                        }
                      }}
                      style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: selectedSurveys.includes(submission.submission_id) 
                          ? '#e3f2fd' 
                          : selectedSubmissionId === submission.submission_id 
                            ? '#f0f7ff' 
                            : 'transparent',
                        cursor: selectionMode ? 'pointer' : 'default'
                      }}
                    >
                      {selectionMode && (
                        <td style={{ padding: '8px', textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={selectedSurveys.includes(submission.submission_id)}
                            onChange={(e) => handleSurveySelection(submission.submission_id, e.target.checked)}
                            style={{ cursor: 'pointer', width: '18px', height: '18px' }}
                          />
                        </td>
                      )}
                      <td style={{ padding: '8px' }}>{submission.email}</td>
                      <td style={{ padding: '8px', maxWidth: '200px' }}>
                        <div style={{ fontWeight: '600' }}>{submission.opportunity_name}</div>
                      </td>
                      <td style={{ padding: '8px', fontSize: '12px' }}>
                        {formatDate(submission.submitted_at)}
                      </td>
                      <td style={{ padding: '8px' }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#22c55e', color: 'white', border: '1px solid #22c55e', cursor: 'pointer', transition: 'background-color 0.2s', minWidth: '95px' }}
                            onClick={() => {
                              setSelectedSubmissionId(submission.submission_id);
                              setActiveTab('chart');
                            }}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#16a34a'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#22c55e'}
                          >
                            View Chart
                          </button>
                          <button 
                            className="btn btn-sm btn-primary"
                            style={{ minWidth: '95px' }}
                            onClick={() => openSubmissionEmailModal(submission)}
                          >
                            Email Survey Result
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#dc3545', color: 'white', border: '1px solid #dc3545', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            onClick={() => openDeleteModal(submission)}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#c82333'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#dc3545'}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Companies Tab */}
      {activeTab === 'companies' && (
        <div className="card">
          {/* Upload Section */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '15px' }}>
            <div>
              <h3 style={{ margin: 0, color: '#333' }}>Companies Overview</h3>
              
            </div>
            <div style={{ position: 'relative' }}>
              <input
                type="file"
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleFileUpload}
              />
              <button
                onClick={() => {
                  setShowUploadModal(true);
                  document.body.style.overflow = 'hidden';
                }}
                disabled={uploading}
                style={{
                  backgroundColor: uploading ? '#6c757d' : '#28a745',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '50px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: uploading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  minWidth: '50px',
                  minHeight: '50px',
                  justifyContent: 'center'
                }}
                title={uploading ? 'Uploading...' : 'Upload Excel file with survey data'}
                onMouseOver={(e) => {
                  if (!uploading) {
                    e.target.style.backgroundColor = '#218838';
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(0,0,0,0.3)';
                  }
                }}
                onMouseOut={(e) => {
                  if (!uploading) {
                    e.target.style.backgroundColor = '#28a745';
                    e.target.style.transform = 'translateY(0px)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
                  }
                }}
              >
                {uploading ? (
                  <>
                    <div style={{
                      width: '18px',
                      height: '18px',
                      border: '2px solid #fff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                  </>
                ) : (
                  <>
                    <span style={{ fontSize: '20px' }}>+</span>
                  </>
                )}
              </button>
              {/* Tooltip */}
              <div style={{
                position: 'absolute',
                bottom: '-45px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: '#333',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '6px',
                fontSize: '12px',
                whiteSpace: 'nowrap',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.3s ease',
                zIndex: 1000
              }}
              className="upload-tooltip"
              >
                Upload Excel with survey data
              </div>
            </div>
          </div>
          
         
          {companies.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <p style={{ fontSize: '18px', color: '#666' }}>No companies found.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa' }}>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>Domain Name</th>
                    <th style={{ padding: '12px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>Submissions</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #dee2e6', minWidth: '300px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {companies.map((company) => (
                    <tr 
                      key={company.domain_name} 
                      style={{ 
                        borderBottom: '1px solid #dee2e6',
                        backgroundColor: selectedCompany === company.domain_name ? '#f0f7ff' : 'transparent'
                      }}
                    >
                      <td style={{ padding: '12px' }}>{capitalizeFirstLetter(company.domain_name)}</td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>{company.submission_count}</td>
                      <td style={{ padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#22c55e', color: 'white', border: '1px solid #22c55e', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            onClick={() => {
                              setSelectedCompany(company.domain_name);
                              setSelectedSubmissionId(null); // Clear any selected submission
                              setActiveTab('chart');
                            }}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#16a34a'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#22c55e'}
                          >
                            View Chart
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#0066cc', color: 'white', border: '1px solid #0066cc', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            onClick={() => exportCompanyCSV(company.domain_name)}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#0052a3'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#0066cc'}
                          >
                            Export CSV
                          </button>
                          <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#ff6b35', color: 'white', border: '1px solid #ff6b35', cursor: 'pointer', transition: 'background-color 0.2s' }}
                            onClick={() => openSurveyResultsModal(company.domain_name)}
                            onMouseOver={e => e.currentTarget.style.backgroundColor = '#e55a2b'}
                            onMouseOut={e => e.currentTarget.style.backgroundColor = '#ff6b35'}
                          >
                            Email Survey Results
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Chart for Selected Submission or Company */}
      {activeTab === 'chart' && (selectedSubmissionId || selectedCompany) && (
        <div className="card">
          {selectedSubmissionId ? (
            <>
              {(() => {
                const selectedSubmission = submissions.find(sub => sub.submission_id === selectedSubmissionId);
                return (
                  <>
                    <div style={{ 
                      padding: '15px', 
                      backgroundColor: '#f8f9fa', 
                      borderRadius: '8px', 
                      marginBottom: '20px',
                      borderLeft: '4px solid #0066cc'
                    }}>
                      <h4 style={{ 
                        margin: '0 0 8px 0', 
                        color: '#0066cc',
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        {selectedSubmission?.opportunity_name || 'AI Opportunity Assessment'}
                      </h4>
                      {selectedSubmission?.opportunity_description ? (
                        <p style={{ 
                          margin: '8px 0 0 0', 
                          color: '#6c757d',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {selectedSubmission.opportunity_description}
                        </p>
                      ) : (
                        <p style={{ 
                          margin: '8px 0 0 0', 
                          color: '#999',
                          fontSize: '13px',
                          fontStyle: 'italic'
                        }}>
                          No description provided for this survey
                        </p>
                      )}
                    </div>
                    <div>
                      <OpportunityChart 
                        submissionId={String(selectedSubmissionId)} 
                        submissionData={selectedSubmission}
                      />
                    </div>
                  </>
                );
              })()}
            </>
          ) : selectedCompany ? (
            <>
              <h2>Company Assessment Chart: {capitalizeFirstLetter(selectedCompany)}</h2>
              <p>Showing all assessments for the selected company</p>
              <div style={{ marginTop: '20px' }}>
                <OpportunityChart companyDomain={selectedCompany} />
              </div>
            </>
          ) : null}
        </div>
      )}
      
      {/* Portfolio Chart Tab */}
      {activeTab === 'portfolio' && (
        <div className="card">
          <h2>Portfolio Overview Chart</h2>
          <p>Strategic positioning of all AI opportunities based on ROI potential and technical feasibility</p>
          <div style={{ marginTop: '20px' }}>
            <OpportunityChart isPortfolio={true} />
          </div>
        </div>
      )}

      {/* Combined Chart Tab */}
      {activeTab === 'combined-chart' && showCombinedChart && selectedSurveys.length > 0 && (
        <div className="card">
          <div style={{ 
            marginBottom: '15px'
          }}>
            <h2>Combined Chart - Selected Surveys</h2>
            <p>Strategic positioning of {selectedSurveys.length} selected AI opportunities</p>
          </div>

          {/* List of selected surveys */}
          <div style={{ 
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #dee2e6'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#333' }}>
              Selected Surveys:
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {selectedSurveys.map(surveyId => {
                const submission = submissions.find(s => s.submission_id === surveyId);
                return submission ? (
                  <div
                    key={surveyId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#e3f2fd',
                      border: '1px solid #2196f3',
                      borderRadius: '20px',
                      fontSize: '13px',
                      color: '#1565c0'
                    }}
                  >
                    <span style={{ fontWeight: '600' }}>{submission.opportunity_name}</span>
                    <span style={{ color: '#64b5f6' }}>•</span>
                    <span style={{ fontSize: '12px' }}>{submission.email}</span>
                    <button
                      onClick={() => handleSurveySelection(surveyId, false)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#1565c0',
                        cursor: 'pointer',
                        padding: '0 4px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        lineHeight: '1'
                      }}
                      title="Remove from selection"
                    >
                      ×
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          </div>

          <div style={{ marginTop: '20px' }}>
            <OpportunityChart selectedSubmissions={selectedSurveys} />
          </div>
        </div>
      )}

      {/* Send Survey Tab */}
      {activeTab === 'send-survey' && (
        <div className="card">
          <h2>Send Survey Invitation</h2>
          <p>Send AI opportunity assessment invitations to specific email addresses</p>
          
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr', 
            gap: '30px', 
            marginTop: '30px',
            alignItems: 'start'
          }}>
            {/* Left Side - Form */}
            <div>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333'
                }}>
                  Email Address *
                </label>
                <input
                  type="email"
                  value={surveyEmail}
                  onChange={(e) => setSurveyEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  disabled={sendingSurveyInvitation}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    backgroundColor: sendingSurveyInvitation ? '#f5f5f5' : 'white',
                    cursor: sendingSurveyInvitation ? 'not-allowed' : 'text'
                  }}
                />
              </div>
              
              <div style={{ marginBottom: '25px' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold', 
                  color: '#333'
                }}>
                  Message
                </label>
                <textarea
                  ref={surveyMessageRef}
                  defaultValue={defaultMessage}
                  placeholder="Enter your message here..."
                  disabled={sendingSurveyInvitation}
                  maxLength={2000}
                  rows={12}
                  onChange={(e) => {
                    setCharacterCount(e.target.value.length);
                  }}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    resize: 'vertical',
                    backgroundColor: sendingSurveyInvitation ? '#f5f5f5' : 'white',
                    cursor: sendingSurveyInvitation ? 'not-allowed' : 'text',
                    fontFamily: 'inherit'
                  }}
                />
                <div style={{ 
                  textAlign: 'right', 
                  fontSize: '12px', 
                  color: '#666', 
                  marginTop: '4px' 
                }}>
                  {characterCount}/2000 characters
                </div>
              </div>
              
              <div style={{ 
                display: 'flex', 
                gap: '15px'
              }}>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setSurveyEmail('');
                    if (surveyMessageRef.current) {
                      surveyMessageRef.current.value = defaultMessage;
                      setCharacterCount(defaultMessage.length);
                    }
                  }}
                  disabled={sendingSurveyInvitation}
                  style={{ flex: '1' }}
                >
                  Clear Form
                </button>
                <button
                  className="btn btn-primary"
                  onClick={sendSurveyInvitation}
                  disabled={sendingSurveyInvitation}
                  style={{ flex: '2' }}
                >
                  {sendingSurveyInvitation ? (
                    <>
                      <span style={{ 
                        display: 'inline-block',
                        width: '16px',
                        height: '16px',
                        border: '2px solid #ffffff',
                        borderTop: '2px solid transparent',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginRight: '8px'
                      }}></span>
                      Sending...
                    </>
                  ) : (
                    'Send Invitation'
                  )}
                </button>
              </div>
            </div>

            {/* Right Side - Information Box */}
            <div style={{ 
              background: '#f8f9fa', 
              padding: '20px', 
              borderRadius: '8px',
              border: '1px solid #dee2e6'
            }}>
              <h4 style={{ color: '#495057', marginTop: '0', marginBottom: '15px' }}>
                What happens when you send an invitation:
              </h4>
              <p style={{ color: '#6c757d', marginBottom: '10px', fontSize: '14px' }}>
                • Recipient receives a beautifully formatted email invitation<br />
                • Your message is included in the email body<br />
                • Direct link to the survey is included for easy access<br />
                • Recipients complete the survey and get instant AI opportunity analysis
              </p>
            </div>
          </div>
        </div>
      )}



      {/* Email Modal */}
      {showEmailModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '30px', 
            maxWidth: '600px', 
            width: '90%', 
            maxHeight: '80vh', 
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Send Emails - {selectedDomainForEmail}</h3>
              <button 
                onClick={closeEmailModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>
            
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Select email addresses to send company-wide AI portfolio updates to users from {selectedDomainForEmail}.
            </p>

            {loadingEmails ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '16px', marginBottom: '10px' }}>Loading email addresses...</div>
                <div style={{ 
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #3498db',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </div>
            ) : (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', fontWeight: 'bold' }}>
                  <input
                    type="checkbox"
                    checked={selectedEmails.length === domainEmails.length && domainEmails.length > 0}
                    onChange={(e) => handleSelectAllEmails(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Select All ({domainEmails.length} unique emails)
                </label>
                
                <div style={{ 
                  maxHeight: '300px', 
                  overflow: 'auto', 
                  border: '1px solid #ddd', 
                  borderRadius: '4px',
                  padding: '10px'
                }}>
                  {domainEmails.map((email, index) => (
                    <label 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        marginBottom: '12px',
                        padding: '12px',
                        backgroundColor: selectedEmails.includes(email) ? '#f0f7ff' : 'transparent',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        border: selectedEmails.includes(email) ? '1px solid #0066cc' : '1px solid transparent'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedEmails.includes(email)}
                        onChange={(e) => handleEmailSelection(email, e.target.checked)}
                        style={{ marginRight: '12px' }}
                      />
                      <div style={{ fontWeight: '600', color: '#333' }}>{email}</div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ 
              display: 'flex', 
              gap: '10px', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #eee',
              paddingTop: '20px'
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={closeEmailModal}
                disabled={sendingEmails}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={sendCompanyEmails}
                disabled={selectedEmails.length === 0 || sendingEmails}
              >
                {sendingEmails ? 'Sending...' : `Send Emails (${selectedEmails.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submission Email Modal */}
      {showSubmissionEmailModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '30px', 
            maxWidth: '700px', 
            width: '90%', 
            maxHeight: '90vh', 
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Email Survey Results - {selectedSubmissionForEmail?.opportunity_name}</h3>
              <button 
                onClick={closeSubmissionEmailModal}
                disabled={sendingSubmissionEmail}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>
            
            <p style={{ color: '#666', marginBottom: '20px' }}>
              Review and customize the email before sending assessment results.
            </p>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                color: '#333'
              }}>
                To: *
              </label>
              <input
                type="text"
                value={submissionEmailToField}
                onChange={(e) => setSubmissionEmailToField(e.target.value)}
                placeholder="recipient1@example.com, recipient2@example.com"
                disabled={sendingSubmissionEmail}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: sendingSubmissionEmail ? '#f5f5f5' : 'white',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'text'
                }}
              />
              <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                Separate multiple emails with commas
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                color: '#333'
              }}>
                CC: (optional)
              </label>
              <input
                type="text"
                value={submissionEmailCcField}
                onChange={(e) => setSubmissionEmailCcField(e.target.value)}
                placeholder="cc@example.com"
                disabled={sendingSubmissionEmail}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: sendingSubmissionEmail ? '#f5f5f5' : 'white',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                color: '#333'
              }}>
                Subject: *
              </label>
              <input
                type="text"
                value={submissionEmailSubject}
                onChange={(e) => setSubmissionEmailSubject(e.target.value)}
                placeholder="Email subject"
                disabled={sendingSubmissionEmail}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  backgroundColor: sendingSubmissionEmail ? '#f5f5f5' : 'white',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'text'
                }}
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                color: '#333'
              }}>
                Message: *
              </label>
              <textarea
                value={submissionEmailMessage}
                onChange={(e) => setSubmissionEmailMessage(e.target.value)}
                placeholder="Enter your message here..."
                disabled={sendingSubmissionEmail}
                rows={15}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px',
                  resize: 'vertical',
                  backgroundColor: sendingSubmissionEmail ? '#f5f5f5' : 'white',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'text',
                  fontFamily: 'inherit'
                }}
              />
              <div style={{ 
                textAlign: 'right', 
                fontSize: '12px', 
                color: '#666', 
                marginTop: '4px' 
              }}>
                {submissionEmailMessage.length} characters
              </div>
            </div>

            <div style={{ 
              background: '#e3f2fd', 
              padding: '15px', 
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '14px',
              color: '#1976d2'
            }}>
              <strong>Note:</strong> The email will include a link to view interactive results and a visual chart attachment.
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={closeSubmissionEmailModal}
                disabled={sendingSubmissionEmail}
                style={{
                  padding: '12px 24px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  backgroundColor: 'white',
                  color: '#333',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: sendingSubmissionEmail ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendSubmissionEmail}
                disabled={sendingSubmissionEmail}
                style={{
                  padding: '12px 24px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: sendingSubmissionEmail ? '#ccc' : '#007bff',
                  color: 'white',
                  cursor: sendingSubmissionEmail ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                {sendingSubmissionEmail ? 'Sending...' : 'Send Email'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Survey Results Email Modal */}
      {showSurveyResultsModal && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '30px', 
            maxWidth: '900px', 
            width: '90%', 
            maxHeight: '90vh', 
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#333' }}>Email Survey Results - {capitalizeFirstLetter(selectedDomainForResults)}</h3>
              <button 
                onClick={closeSurveyResultsModal}
                disabled={sendingSurveyResults}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: sendingSurveyResults ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>
            
            {loadingSurveyResultsEmails ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                <div style={{ fontSize: '16px', marginBottom: '10px' }}>Loading email addresses...</div>
                <div style={{ 
                  display: 'inline-block',
                  width: '20px',
                  height: '20px',
                  border: '2px solid #f3f3f3',
                  borderTop: '2px solid #3498db',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
              </div>
            ) : (
              <>
                <p style={{ color: '#666', marginBottom: '20px' }}>
                  Review and customize the email before sending survey results to {capitalizeFirstLetter(selectedDomainForResults)}.
                </p>

                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '30px', 
                  marginBottom: '25px'
                }}>
                  {/* Left Side - Email Form */}
                  <div>
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: 'bold', 
                        color: '#333'
                      }}>
                        To: *
                      </label>
                      <input
                        type="text"
                        value={surveyResultsToField}
                        onChange={(e) => setSurveyResultsToField(e.target.value)}
                        placeholder="recipient1@example.com, recipient2@example.com"
                        disabled={sendingSurveyResults}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          backgroundColor: sendingSurveyResults ? '#f5f5f5' : 'white',
                          cursor: sendingSurveyResults ? 'not-allowed' : 'text'
                        }}
                      />
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Separate multiple emails with commas
                      </div>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: 'bold', 
                        color: '#333'
                      }}>
                        CC: (optional)
                      </label>
                      <input
                        type="text"
                        value={surveyResultsCcField}
                        onChange={(e) => setSurveyResultsCcField(e.target.value)}
                        placeholder="cc@example.com"
                        disabled={sendingSurveyResults}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          backgroundColor: sendingSurveyResults ? '#f5f5f5' : 'white',
                          cursor: sendingSurveyResults ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: 'bold', 
                        color: '#333'
                      }}>
                        Subject: *
                      </label>
                      <input
                        type="text"
                        value={surveyResultsSubject}
                        onChange={(e) => setSurveyResultsSubject(e.target.value)}
                        placeholder="Email subject"
                        disabled={sendingSurveyResults}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          backgroundColor: sendingSurveyResults ? '#f5f5f5' : 'white',
                          cursor: sendingSurveyResults ? 'not-allowed' : 'text'
                        }}
                      />
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ 
                        display: 'block', 
                        marginBottom: '8px', 
                        fontWeight: 'bold', 
                        color: '#333'
                      }}>
                        Message: *
                      </label>
                      <textarea
                        value={surveyResultsMessage}
                        onChange={(e) => setSurveyResultsMessage(e.target.value)}
                        placeholder="Enter your message here..."
                        disabled={sendingSurveyResults}
                        rows={12}
                        style={{
                          width: '100%',
                          padding: '12px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px',
                          resize: 'vertical',
                          backgroundColor: sendingSurveyResults ? '#f5f5f5' : 'white',
                          cursor: sendingSurveyResults ? 'not-allowed' : 'text',
                          fontFamily: 'inherit'
                        }}
                      />
                      <div style={{ 
                        textAlign: 'right', 
                        fontSize: '12px', 
                        color: '#666', 
                        marginTop: '4px' 
                      }}>
                        {surveyResultsMessage.length} characters
                      </div>
                    </div>
                  </div>

                  {/* Right Side - Email Selection */}
                  <div>
                    <div style={{ 
                      background: '#f8f9fa', 
                      padding: '20px', 
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      marginBottom: '20px'
                    }}>
                      <h4 style={{ color: '#495057', marginTop: '0', marginBottom: '15px' }}>
                        Select Recipients
                      </h4>
                      
                      <label style={{ display: 'flex', alignItems: 'center', marginBottom: '15px', fontWeight: 'bold' }}>
                        <input
                          type="checkbox"
                          checked={selectedResultsEmails.length === availableEmailsForResults.length && availableEmailsForResults.length > 0}
                          onChange={(e) => handleSelectAllResultsEmails(e.target.checked)}
                          disabled={sendingSurveyResults}
                          style={{ marginRight: '8px' }}
                        />
                        Select All ({availableEmailsForResults.length} emails)
                      </label>
                      
                      <div style={{ 
                        maxHeight: '300px', 
                        overflow: 'auto', 
                        border: '1px solid #ddd', 
                        borderRadius: '4px',
                        padding: '10px',
                        backgroundColor: 'white'
                      }}>
                        {availableEmailsForResults.map((email, index) => (
                          <label 
                            key={index} 
                            style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              marginBottom: '12px',
                              padding: '12px',
                              backgroundColor: selectedResultsEmails.includes(email) ? '#e3f2fd' : 'transparent',
                              borderRadius: '4px',
                              cursor: sendingSurveyResults ? 'not-allowed' : 'pointer',
                              border: selectedResultsEmails.includes(email) ? '1px solid #0066cc' : '1px solid transparent'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedResultsEmails.includes(email)}
                              onChange={(e) => handleResultsEmailSelection(email, e.target.checked)}
                              disabled={sendingSurveyResults}
                              style={{ marginRight: '12px' }}
                            />
                            <div style={{ fontWeight: '500', color: '#333', fontSize: '14px' }}>{email}</div>
                          </label>
                        ))}
                      </div>
                    </div>

                    <div style={{ 
                      background: '#fff3cd', 
                      padding: '15px', 
                      borderRadius: '8px',
                      border: '1px solid #ffeaa7'
                    }}>
                      <div style={{ fontSize: '14px', color: '#856404' }}>
                        <strong>Note:</strong> You can manually add or remove email addresses in the To and CC fields above.
                      </div>
                    </div>
                  </div>
                </div>

                <div style={{ 
                  display: 'flex', 
                  gap: '15px', 
                  justifyContent: 'flex-end',
                  borderTop: '1px solid #eee',
                  paddingTop: '20px'
                }}>
                  <button 
                    className="btn btn-secondary" 
                    onClick={closeSurveyResultsModal}
                    disabled={sendingSurveyResults}
                    style={{ flex: '1' }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary" 
                    onClick={sendSurveyResultsEmail}
                    disabled={sendingSurveyResults}
                    style={{ flex: '2' }}
                  >
                    {sendingSurveyResults ? (
                      <>
                        <span style={{ 
                          display: 'inline-block',
                          width: '16px',
                          height: '16px',
                          border: '2px solid #ffffff',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite',
                          marginRight: '8px'
                        }}></span>
                        Sending...
                      </>
                    ) : (
                      'Send Email'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && submissionToDelete && (
        <div style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1001 
        }}>
          <div style={{ 
            backgroundColor: 'white', 
            borderRadius: '8px', 
            padding: '30px', 
            maxWidth: '500px', 
            width: '90%', 
            boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
            border: '2px solid #dc3545'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, color: '#dc3545', fontSize: '20px' }}>⚠️ Confirm Deletion</h3>
              <button 
                onClick={closeDeleteModal}
                disabled={deletingSubmission}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: deletingSubmission ? 'not-allowed' : 'pointer',
                  color: '#666',
                  padding: '5px'
                }}
              >
                ×
              </button>
            </div>
            
            <div style={{ marginBottom: '25px' }}>
              <p style={{ color: '#333', marginBottom: '15px', lineHeight: '1.6' }}>
                Are you sure you want to <strong>permanently delete</strong> this survey submission?
              </p>
              
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '6px',
                border: '1px solid #dee2e6',
                marginBottom: '15px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Opportunity:</strong> {submissionToDelete.opportunity_name}
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <strong>Email:</strong> {submissionToDelete.email}
                </div>
                <div>
                  <strong>Submitted:</strong> {formatDate(submissionToDelete.submitted_at)}
                </div>
              </div>
              
              <div style={{ 
                backgroundColor: '#fff3cd', 
                color: '#856404',
                padding: '12px', 
                borderRadius: '6px',
                border: '1px solid #ffeaa7',
                fontSize: '14px'
              }}>
                <strong>Warning:</strong> This action cannot be undone. All associated data including scores, answers, and charts will be permanently removed.
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '15px', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #eee',
              paddingTop: '20px'
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={closeDeleteModal}
                disabled={deletingSubmission}
                style={{ flex: '1' }}
              >
                Cancel
              </button>
              <button 
                className="btn"
                style={{ 
                  backgroundColor: '#dc3545', 
                  color: 'white', 
                  border: '1px solid #dc3545',
                  flex: '1',
                  cursor: deletingSubmission ? 'not-allowed' : 'pointer',
                  opacity: deletingSubmission ? 0.6 : 1
                }}
                onClick={deleteSubmission}
                disabled={deletingSubmission}
              >
                {deletingSubmission ? (
                  <>
                    <span style={{ 
                      display: 'inline-block',
                      width: '16px',
                      height: '16px',
                      border: '2px solid #ffffff',
                      borderTop: '2px solid transparent',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginRight: '8px'
                    }}></span>
                    Deleting...
                  </>
                ) : (
                  'Delete Permanently'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Excel Modal */}
      {showUploadModal && (
        <div className="modal-overlay" style={{ 
          position: 'fixed', 
          top: 0, 
          left: 0, 
          right: 0, 
          bottom: 0, 
          backgroundColor: 'rgba(0,0,0,0.5)', 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          zIndex: 1000 
        }}>
          <div className="modal-content" style={{ 
            backgroundColor: 'white', 
            borderRadius: '12px', 
            padding: '25px', 
            maxWidth: '550px', 
            width: '90%', 
            maxHeight: '70vh', 
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#333', fontSize: '22px' }}>Upload Survey Data</h2>
              <button 
                onClick={closeUploadModal}
                style={{ 
                  background: 'none', 
                  border: 'none', 
                  fontSize: '24px', 
                  cursor: 'pointer',
                  color: '#666',
                  padding: '5px',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#2c3e50', marginBottom: '12px', fontSize: '16px' }}>📋 Excel File Requirements</h3>
              
              <div style={{ 
                backgroundColor: '#f8f9fa', 
                padding: '15px', 
                borderRadius: '8px',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: '0 0 12px 0', fontWeight: '600', color: '#495057', fontSize: '14px' }}>
                  Your Excel file should contain the following columns:
                </p>
                
                <ul style={{ margin: '0', paddingLeft: '18px', color: '#6c757d', fontSize: '13px', lineHeight: '1.4' }}>
                  <li style={{ marginBottom: '4px' }}><strong>Email:</strong> Participant's email address</li>
                  <li style={{ marginBottom: '4px' }}><strong>Opportunity Name:</strong> Name of the AI opportunity/use case</li>
                  <li style={{ marginBottom: '4px' }}><strong>Domain:</strong> Company domain (e.g., company.com)</li>
                  <li style={{ marginBottom: '4px' }}><strong>Score columns:</strong> Technical Feasibility, Strategic Value, Implementation Complexity, Data Quality, Risk Assessment</li>
                  <li><strong>Answer columns:</strong> Detailed responses for each assessment question</li>
                </ul>
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              justifyContent: 'flex-end',
              borderTop: '1px solid #eee',
              paddingTop: '18px'
            }}>
              <button 
                className="btn btn-secondary" 
                onClick={closeUploadModal}
                style={{ flex: '1' }}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleUploadFromModal}
                style={{ 
                  flex: '1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                Choose Excel File
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default AdminDashboard;